// React rendering
import { StrictMode } from "react"
import { createRoot } from "react-dom/client"

// Router
import { createRoutesFromElements, Route, RouterProvider } from "@/lib/router"

// API
import { apiUrlOverride } from "@/api/rest"

// Tracing & Logging
import { initSentry, sentryCreateBrowserRouter } from "@/lib/sentry"

// Translations — must initialize before any component calls useTranslation
import { initI18n } from "@/i18n/browser"
void initI18n()

// Styles
import "@/styles/index.css"

// UI
import {
	App,
	RedirectToHomePage,
	RedirectToLogin,
	RedirectToNewMonthlyOverview,
} from "./App"

// env variables
import { MOCK_BACKEND, PREVIEW_NAME } from "@/lib/env"

// Layouts / Templates
import { ReloadPrompt } from "@/components/dialogs/ReloadPrompt"
import { PreviewBar } from "@/components/PreviewBar"
import { PageTemplate } from "@/layouts/PageTemplate"

// Routes
import { ConditionalRoute } from "@/components/ConditionalRoute"
import { UserRoles } from "@/constants/constants"
import { Routes } from "@/constants/routes"

// Generic pages
import { NotFound } from "@/pages/NotFound"
import { Info } from "@/pages/profile/info"

// Statistics pages
import { StatisticsPlatform } from "@/pages/statistics/platform"
import { StatisticsImpact } from "@/pages/statistics/impact"
import { StatisticsProjects } from "@/pages/statistics/projects"
import { StatisticsDefaultRateInfo } from "@/pages/statistics/default"
import { SingleProjectStats } from "@/pages/statistics/project"

// Project pages
import { ProjectLayout } from "@/pages/project"
import { ProjectCommunications } from "@/pages/project/pages/Communications"
import { ProjectDashboard } from "@/pages/project/pages/ProjectDashboard"
import { ProjectDocumentation } from "@/pages/project/pages/Documentation"
import { ProjectNotifications } from "@/pages/project/pages/Notifications"
import { ProjectProduction } from "@/pages/project/pages/Production"
import { ProjectRevenue } from "@/pages/project/pages/Revenue"

// TODO: import { Recent } from "@/pages/Recent"

// Profile pages TODO: Cleanup code
import {
	ProfileLayout,
	ProfilePersonalData,
	ProfileBankDetails,
	SettingsApp,
} from "@/pages/profile"
import { ProfileAccount } from "@/pages/profile/pages/account"
import { ProfileNotifications } from "@/pages/profile/pages/ProfileNotifications"
import { ProfileCompanyDetails } from "@/pages/profile/pages/company-details"

// Investor pages
import { InvestorLayout } from "@/pages/investor"
import { InvestorProfile } from "@/pages/investor/pages/profile"
import { InvestorTest } from "@/pages/investor/pages/InvestorTest"
import { InvestorFunds } from "@/pages/investor/pages/InvestorFunds"
import { InvestorIdentity } from "@/pages/investor/pages/identity"
import { InvestorRisk } from "@/pages/investor/pages/InvestorRisk"
import { InvestorPayments } from "@/pages/investor/pages/InvestorPayments"

// Investments pages
import { InvestmentsLayout, InvestmentsIndex } from "@/pages/investments"
import { Fiscal } from "@/pages/investments/Fiscal"
import { Production } from "@/pages/investments/production"
import { ProvisionalInvestments } from "@/pages/investments/Investments"
import { MonthlyOverview } from "@/pages/investments/monthly-overview"
import { InvestmentsProjectsCurrentOverview } from "@/pages/investments/current-overview"

// Payments pages / Finance
import { AllPaymentsOverview } from "@/pages/finance/payments"
import { PaymentDetailProjectOwner } from "@/pages/finance/payments/owner"
import { PaymentDetailEnergySupplier } from "@/pages/finance/payments/supplier"
import { FinanceDashboard } from "@/pages/finance/projects"
import { FinancePerformance } from "@/pages/finance/pages/Performance"
import { FinanceSolarInterestTariff } from "@/pages/finance/solar-interest-tariff"
import { FinanceBondsLoan } from "@/pages/finance/pages/FinanceBondsLoan"

// Communications
import { AllCommunications, MessagesLayout } from "@/pages/profile/messages"
import { Message } from "@/pages/project/pages/Message"
import { AllPayments } from "@/pages/investments/payments"
import { RecentUserActivity } from "@/components/RecentActivity"

// Community pages
import { CommunityLayout } from "./pages/community"
import { CommunitySharePage } from "./pages/community/share"

async function registerMockApi() {
	// do not mock api backend when url from flag from url has been set
	if (Boolean(apiUrlOverride)) {
		return
	}

	const enableMocks =
		new URL(window.location.href).searchParams.get("enableMocks") === "true"

	if (MOCK_BACKEND === "true" && enableMocks) {
		const { worker } = await import("./mocks/browser")

		// `onUnhandledRequest` will remove the warning on unhandled requests
		// (such as static assets, manifest, favicon)
		await worker.start({ onUnhandledRequest: "bypass" })
		console.info("msw enabled")
	}
}

/**
 * main
 */
async function main() {
	// Register Mock Service Worker to fake API
	try {
		await registerMockApi()
	} catch (error) {
		console.error("Error registering mock API:", error)
	}

	const root = createRoot(document.getElementById("root") ?? document.body)

	// Initialise Sentry
	initSentry()

	/**
	 * Router
	 *
	 * Due to the decoupling of fetching and rendering in the design
	 * of the data APIs, you should create your router outside of the
	 * React tree with a statically defined set of routes. For more
	 * information on this design, please see the Remixing React Router
	 * blog post and the When to Fetch conference talk.
	 */
	const router = sentryCreateBrowserRouter(
		createRoutesFromElements(
			<Route
				path={Routes.Home}
				errorElement={
					<App>
						<NotFound />
					</App>
				}
				element={<App />}
			>
				{/** Redirect to home when root route */}
				<Route path={Routes.Home} element={<RedirectToHomePage />} />

				{/** Statistics pages */}
				<Route path="stats">
					<Route
						path={Routes.StatisticsPlatform}
						element={<StatisticsPlatform />}
					/>
					<Route
						path={Routes.StatisticsImpact}
						element={<StatisticsImpact />}
					/>
					<Route
						path={Routes.StatisticsProjects}
						element={<StatisticsProjects />}
					/>
					<Route
						path={Routes.StatisticsDefaultRate}
						element={<StatisticsDefaultRateInfo />}
					/>
					<Route
						path={Routes.StatisticsPlatformSingleProject}
						element={<SingleProjectStats />}
					/>
				</Route>

				{/** Profile pages */}
				<Route path={Routes.SettingsProfilePersonalDetails}>
					<Route
						path={Routes.SettingsProfilePersonalDetails}
						element={
							<ConditionalRoute
								auth={
									<ProfileLayout>
										<ProfilePersonalData />
									</ProfileLayout>
								}
								unAuth={<RedirectToLogin />}
							/>
						}
					/>
					<Route
						path={Routes.SettingsProfileAccount}
						element={
							<ConditionalRoute
								auth={
									<ProfileLayout>
										<ProfileAccount />
									</ProfileLayout>
								}
								unAuth={<RedirectToLogin />}
							/>
						}
					/>
					<Route
						path={Routes.SettingsProfileCompanyDetails}
						element={
							<ConditionalRoute
								auth={
									<ProfileLayout>
										<ProfileCompanyDetails />
									</ProfileLayout>
								}
								unAuth={<RedirectToLogin />}
							/>
						}
					/>
					<Route
						path={Routes.SettingsProfileBankDetails}
						element={
							<ConditionalRoute
								auth={
									<ProfileLayout>
										<ProfileBankDetails />
									</ProfileLayout>
								}
								unAuth={<RedirectToLogin />}
							/>
						}
					/>

					<Route
						path={Routes.Recent}
						element={
							<ConditionalRoute
								auth={
									<ProfileLayout>
										<RecentUserActivity />
									</ProfileLayout>
								}
								unAuth={<RedirectToLogin />}
							/>
						}
					/>
					<Route
						path={Routes.AppSettings}
						element={
							<ConditionalRoute
								auth={
									<ProfileLayout>
										<SettingsApp />
									</ProfileLayout>
								}
								unAuth={<RedirectToLogin />}
							/>
						}
					/>
				</Route>

				{/** Investor profile */}
				<Route path={Routes.InvestorProfile}>
					<Route
						path={Routes.InvestorProfile}
						element={
							<ConditionalRoute
								auth={
									<InvestorLayout>
										<InvestorProfile />
									</InvestorLayout>
								}
								unAuth={<RedirectToLogin />}
							/>
						}
					/>
					<Route
						path={Routes.InvestorTest}
						element={
							<ConditionalRoute
								auth={
									<InvestorLayout>
										<InvestorTest />
									</InvestorLayout>
								}
								unAuth={<RedirectToLogin />}
							/>
						}
					/>
					<Route
						path={Routes.InvestorRisk}
						element={
							<ConditionalRoute
								auth={
									<InvestorLayout>
										<InvestorRisk />
									</InvestorLayout>
								}
								unAuth={<RedirectToLogin />}
							/>
						}
					/>
					<Route
						path={Routes.InvestorFunds}
						element={
							<ConditionalRoute
								auth={
									<InvestorLayout>
										<InvestorFunds />
									</InvestorLayout>
								}
								unAuth={<RedirectToLogin />}
							/>
						}
					/>
					<Route
						path={Routes.InvestorPayments}
						element={
							<ConditionalRoute
								auth={
									<InvestorLayout>
										<InvestorPayments />
									</InvestorLayout>
								}
								unAuth={<RedirectToLogin />}
							/>
						}
					/>
					<Route
						path={Routes.InvestorIdentity}
						element={
							<ConditionalRoute
								auth={
									<InvestorLayout>
										<InvestorIdentity />
									</InvestorLayout>
								}
								unAuth={<RedirectToLogin />}
							/>
						}
					/>
				</Route>
				<Route
					path={Routes.Info}
					element={
						<ConditionalRoute
							roles={[UserRoles.admin]}
							auth={<Info />}
							unAuth={<RedirectToLogin />}
						/>
					}
				/>

				{/** Communications */}
				<Route path={Routes.Communications}>
					<Route
						path={Routes.Communications}
						element={
							<ConditionalRoute
								auth={
									<MessagesLayout>
										<AllCommunications />
									</MessagesLayout>
								}
								unAuth={<RedirectToLogin />}
							/>
						}
					/>
					<Route
						path={Routes.CommunicationsSettingsNotifications}
						element={
							<ConditionalRoute
								auth={
									<MessagesLayout>
										<ProfileNotifications />
									</MessagesLayout>
								}
								unAuth={<RedirectToLogin />}
							/>
						}
					/>
					<Route
						path={Routes.CommunicationsMessage}
						element={
							<ConditionalRoute
								auth={
									<MessagesLayout>
										<Message />
									</MessagesLayout>
								}
								unAuth={<RedirectToLogin />}
							/>
						}
					/>
				</Route>

				{/** Network */}
				<Route
					path={Routes.CommunityShare}
					element={
						<ConditionalRoute
							auth={
								<CommunityLayout>
									<CommunitySharePage />
								</CommunityLayout>
							}
							unAuth={<RedirectToLogin />}
						/>
					}
				/>

				{/** Investments pages */}
				<Route path="investments">
					<Route path={Routes.InvestmentsProjects}>
						<Route
							path={Routes.InvestmentsProjects}
							element={
								<ConditionalRoute
									auth={
										<InvestmentsLayout>
											<InvestmentsIndex />
										</InvestmentsLayout>
									}
									unAuth={<RedirectToLogin />}
								/>
							}
						/>
						<Route
							path={Routes.InvestmentsProjectsDashboardPaginated}
							element={
								<ConditionalRoute
									auth={
										<InvestmentsLayout>
											<InvestmentsIndex />
										</InvestmentsLayout>
									}
									unAuth={<RedirectToLogin />}
								/>
							}
						/>
					</Route>
					<Route
						path={Routes.ProvisionalInvestments}
						element={
							<ConditionalRoute
								auth={
									<InvestmentsLayout>
										<ProvisionalInvestments />
									</InvestmentsLayout>
								}
								unAuth={<RedirectToLogin />}
							/>
						}
					/>
					<Route
						path={Routes.InvestmentsProjectsCurrentOverview}
						element={
							<ConditionalRoute
								auth={
									<InvestmentsLayout>
										<InvestmentsProjectsCurrentOverview />
									</InvestmentsLayout>
								}
								unAuth={<RedirectToLogin />}
							/>
						}
					/>
					<Route
						path={Routes.InvestmentsProjectsFiscalOverview}
						element={
							<ConditionalRoute
								auth={
									<InvestmentsLayout>
										<Fiscal />
									</InvestmentsLayout>
								}
								unAuth={<RedirectToLogin />}
							/>
						}
					/>

					{/** Monthly overview */}
					<Route
						path={
							// KEEP this for backward compatibility
							"/investments/projects/dashboard/monthly-overview/:date"
						}
						element={<RedirectToNewMonthlyOverview />}
					/>
					<Route
						path={Routes.InvestmentsProjectsMonthlyOverviewMain}
						element={
							<ConditionalRoute
								auth={
									<InvestmentsLayout>
										<MonthlyOverview />
									</InvestmentsLayout>
								}
								unAuth={<RedirectToLogin />}
							/>
						}
					>
						<Route
							path={Routes.InvestmentsProjectsMonthlyOverview}
							element={
								<ConditionalRoute
									auth={
										<InvestmentsLayout>
											<MonthlyOverview />
										</InvestmentsLayout>
									}
									unAuth={<RedirectToLogin />}
								/>
							}
						/>
					</Route>

					<Route
						path={Routes.InvestmentsProjectsProduction}
						element={
							<ConditionalRoute
								auth={
									<InvestmentsLayout>
										<Production />
									</InvestmentsLayout>
								}
								unAuth={<RedirectToLogin />}
							/>
						}
					/>
					<Route
						path={Routes.InvestmentsProjectsPayments}
						element={
							<ConditionalRoute
								auth={
									<InvestmentsLayout>
										<AllPayments />
									</InvestmentsLayout>
								}
								unAuth={<RedirectToLogin />}
							/>
						}
					/>

					{/** Project pages */}
					<Route path={`${Routes.InvestmentsProjects}/:id/`}>
						<Route
							path={Routes.InvestmentsProjectDashboard}
							element={
								<ConditionalRoute
									auth={
										<ProjectLayout>
											<ProjectDashboard />
										</ProjectLayout>
									}
									unAuth={<RedirectToLogin />}
								/>
							}
						/>
						<Route
							path={Routes.InvestmentsProjectRevenue}
							element={
								<ConditionalRoute
									auth={
										<ProjectLayout>
											<ProjectRevenue />
										</ProjectLayout>
									}
									unAuth={<RedirectToLogin />}
								/>
							}
						/>
						<Route
							path={Routes.InvestmentsProjectNotifications}
							element={
								<ConditionalRoute
									auth={
										<ProjectLayout>
											<ProjectNotifications />
										</ProjectLayout>
									}
									unAuth={<RedirectToLogin />}
								/>
							}
						/>
						<Route
							path={Routes.InvestmentsProjectData}
							element={
								<ConditionalRoute
									auth={
										<ProjectLayout>
											<ProjectProduction />
										</ProjectLayout>
									}
									unAuth={<RedirectToLogin />}
								/>
							}
						/>
						<Route
							path={Routes.InvestmentsProjectCommunications}
							element={
								<ConditionalRoute
									auth={
										<ProjectLayout>
											<ProjectCommunications />
										</ProjectLayout>
									}
									unAuth={<RedirectToLogin />}
								/>
							}
						/>
						<Route
							path={Routes.InvestmentsProjectDocumentation}
							element={
								<ConditionalRoute
									auth={
										<ProjectLayout>
											<ProjectDocumentation />
										</ProjectLayout>
									}
									unAuth={<RedirectToLogin />}
								/>
							}
						/>
					</Route>
				</Route>

				{/** Finance */}
				<Route path="finance">
					{/** Finance projects */}
					<Route path={Routes.FinanceProjects}>
						<Route
							path={Routes.FinanceProjects}
							element={
								<ConditionalRoute
									roles={[
										UserRoles.admin,
										UserRoles.projectOwner,
										UserRoles.energyProvider,
									]}
									auth={<FinanceDashboard />}
									unAuth={<RedirectToLogin />}
								/>
							}
						/>
						<Route
							path={`${Routes.FinanceProjects}/:id`}
							element={
								<ConditionalRoute
									roles={[
										UserRoles.admin,
										UserRoles.projectOwner,
										UserRoles.energyProvider,
									]}
									auth={<FinancePerformance />}
									unAuth={<RedirectToLogin />}
								/>
							}
						/>
					</Route>

					{/** Payments pages */}
					<Route path={Routes.FinanceInterestPayments}>
						<Route
							path={Routes.FinanceInterestPayments}
							element={
								<ConditionalRoute
									roles={[
										UserRoles.admin,
										UserRoles.projectOwner,
										UserRoles.energyProvider,
									]}
									auth={
										<PageTemplate>
											<AllPaymentsOverview />
										</PageTemplate>
									}
									unAuth={<RedirectToLogin />}
								/>
							}
						/>

						<Route
							path={`${Routes.FinanceInterestPayments}/supplier/:id`}
							element={
								<ConditionalRoute
									roles={[
										UserRoles.admin,
										UserRoles.energyProvider,
									]}
									auth={
										<PageTemplate>
											<PaymentDetailEnergySupplier />
										</PageTemplate>
									}
									unAuth={<RedirectToLogin />}
								/>
							}
						/>
						<Route
							path={`${Routes.FinanceInterestPayments}/owner/:id`}
							element={
								<ConditionalRoute
									roles={[
										UserRoles.admin,
										UserRoles.projectOwner,
									]}
									auth={
										<PageTemplate>
											<PaymentDetailProjectOwner />
										</PageTemplate>
									}
									unAuth={<RedirectToLogin />}
								/>
							}
						/>
					</Route>
					<Route
						path={`${Routes.FinanceSolarInterestTariff}/:id`}
						element={
							<ConditionalRoute
								roles={[
									UserRoles.admin,
									UserRoles.projectOwner,
									UserRoles.energyProvider,
								]}
								auth={<FinanceSolarInterestTariff />}
								unAuth={<RedirectToLogin />}
							/>
						}
					/>
					<Route
						path={`${Routes.FinanceBondsLoan}/:id`}
						element={
							<ConditionalRoute
								roles={[
									UserRoles.admin,
									UserRoles.projectOwner,
									UserRoles.energyProvider,
								]}
								auth={<FinanceBondsLoan />}
								unAuth={<RedirectToLogin />}
							/>
						}
					/>
				</Route>
			</Route>,
		),
	)

	// Render root
	root.render(
		<StrictMode>
			{/** Reload prompt. Note: We register here to avoid re-renders */}
			<ReloadPrompt />

			{PREVIEW_NAME && <PreviewBar name={PREVIEW_NAME} />}
			<RouterProvider router={router} />
		</StrictMode>,
	)
}

// Run main function
void main()
